import React, {useEffect, useState, useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {apiRequest} from "../api";
import TourFilter from "./TourFilter";

const TourList = ({universityId, onEdit, refreshKey}) => {
    const [tours, setTours] = useState([]);
    const [error, setError] = useState(null);
    const [feedbackMessage, setFeedbackMessage] = useState(null);
    const [deletingTourId, setDeletingTourId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [tourToDelete, setTourToDelete] = useState(null);
    const [filters, setFilters] = useState({
        name: "",
        country: "",
        createdBy: ""
    });

    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_BASE_URL;
    const role = localStorage.getItem("role");
    const token = localStorage.getItem("token"); // Get token for authenticated requests

    // Function to format dates in Pacific Time
    const formatDate = (date) => {
        if (!date) return "No Date";
        // Parse the date as UTC and format it in Pacific Time
        return new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            year: "numeric",
            month: "short",
            day: "numeric",
        }).format(new Date(date + "Z")); // Append "Z" to ensure it's treated as UTC
    };

    // Filter tours based on applied filters
    const filteredTours = useMemo(() => {
        return tours.filter(tour => {
            // Filter by name (case-insensitive)
            if (filters.name && !tour.name.toLowerCase().includes(filters.name.toLowerCase())) {
                return false;
            }

            // Filter by country (exact match)
            if (filters.country && tour.country !== filters.country) {
                return false;
            }

            // Filter by created by (case-insensitive)
            if (filters.createdBy && tour.created_by && !tour.created_by.toLowerCase().includes(filters.createdBy.toLowerCase())) {
                return false;
            }

            return true;
        });
    }, [tours, filters]);

    // Fetch tours
    const fetchTours = async () => {
        try {
            const endpoint = `${apiUrl}/tours`;
            const data = await apiRequest(endpoint, "GET", null, token);
            setTours(data);
        } catch (error) {
            console.error("Error fetching tours:", error);
            setError("Failed to load tours. Please try again.");
        }
    };
    const handleDelete = async (tourId) => {
        setDeletingTourId(tourId);
        try {
            const url = `${apiUrl}/tours/${tourId}`;
            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error("Failed to delete tour.");
            }
            setFeedbackMessage("Tour deleted successfully.");
            fetchTours(); // Refresh the tour list
        } catch (error) {
            console.error("Error deleting tour:", error);
            setFeedbackMessage("Failed to delete tour. Please try again.");
        } finally {
            setDeletingTourId(null);
        }
    };

    const openDeleteModal = (tour) => {
        setTourToDelete(tour);
        setShowDeleteModal(true);
    };
    const syncCRM = async (tourId) => {
        try {
            const url = `${apiUrl}/crm/sync/${tourId}`;
            const response = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            if (!response.ok) {
                throw new Error("Failed to sync CRM.");
            }
            setFeedbackMessage("Tour Sync Initialised.");
            fetchTours(); // Refresh the user list
        } catch (error) {
            console.error("Error syncing tour:", error);
            setFeedbackMessage("Failed to sync tour. Please try again.");
        }
    };
    useEffect(() => {
        fetchTours();
    }, [universityId, refreshKey]); // Re-fetch when tourId or refreshKey changes

    if (error) {
        return <p className="text-danger">{error}</p>;
    }

    if (!tours || tours.length === 0) {
        return <p>No tours available for this university.</p>; // No tours case
    }

    if (filteredTours.length === 0 && Object.values(filters).some(v => v !== "")) {
        return (
            <div>
                <TourFilter 
                    filters={filters}
                    onFilterChange={setFilters}
                />
                <div className="tour-summary mb-3">
                    <small className="text-muted">
                        Showing 0 of {tours.length} tours (filtered)
                    </small>
                </div>
                <p>No tours match the current filters. Try adjusting your search criteria.</p>
            </div>
        );
    }
    const handleExport = (tourId) => {
        window.open(`${apiUrl}/submissions/export-tour/${tourId}`);
    };
    return (
        <div>
            {feedbackMessage && <p className="text-success">{feedbackMessage}</p>}
            {error && <p className="text-danger">{error}</p>}
            
            <TourFilter 
                filters={filters}
                onFilterChange={setFilters}
            />
            
            <div className="tour-summary mb-3">
                <small className="text-muted">
                    Showing {filteredTours.length} of {tours.length} tours
                    {Object.values(filters).some(v => v !== "") && " (filtered)"}
                </small>
            </div>
            
            <div className="tours">
                <table>
                    <thead>
                    <tr>
                        <th>TOUR NAME</th>
                        <th>TOUR DATE</th>
                        <th>CREATED DATE</th>
                        <th>CREATED BY</th>
                        <th>LAST UPDATED</th>
                        <th>&nbsp;</th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredTours.map((tour) => (
                        <tr key={tour.id}>
                            <td className="name">{tour.name}</td>
                            <td>
                                {tour.date_from} {tour.date_to}

                            </td>
                            <td>{formatDate(tour.created_on)}</td>
                            <td>{tour.created_by || "Unknown"}</td>

                            <td>{formatDate(tour.last_updated)}</td>
                            <td>
                                <button
                                    className="btn btn-sm btn-secondary btn-edit-tour"
                                    style={{ marginRight: 8 }}
                                    onClick={() => onEdit(tour)}
                                >
                                    Edit
                                </button>
                                <button
                                    className="btn btn-sm btn-info btn-view-events"
                                    style={{ marginRight: 8 }}
                                    onClick={() =>
                                        navigate(`/events/${tour.id}/${encodeURIComponent(tour.name)}`)
                                    }
                                >
                                    View Events
                                </button>
                                {tour.sync_status && tour.sync_status !== "completed" && tour.university && tour.university.crm_enabled === true && (
                                    <button
                                        className="btn btn-sm btn-primary"
                                        style={{ marginRight: 8 }}
                                        onClick={() => syncCRM(tour.id)}
                                    >
                                        <i className="fa fa-sync"></i>
                                    </button>
                                )}
                                <button
                                    className="btn btn-sm btn-warning btn-export-submissions"
                                    style={{ marginRight: 8 }}
                                    onClick={() => handleExport(tour.id)}
                                >
                                    Export Submissions
                                </button>
                                <button
                                    className="btn btn-sm btn-danger btn-delete-tour"
                                    onClick={() => openDeleteModal(tour)}
                                    disabled={deletingTourId === tour.id}
                                >
                                    {deletingTourId === tour.id ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            Deleting...
                                        </>
                                    ) : (
                                        'Delete'
                                    )}
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div
                    style={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        zIndex: 9999,
                    }}
                >
                    <DeleteConfirmationModal
                        item={tourToDelete}
                        itemType="tour"
                        onConfirm={handleDelete}
                        onClose={() => setShowDeleteModal(false)}
                    />
                </div>
            )}
        </div>
    );
};

// Delete Confirmation Modal Component
const DeleteConfirmationModal = ({ item, itemType, onConfirm, onClose }) => {
    const [deleteText, setDeleteText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const handleConfirm = async () => {
        if (deleteText !== 'DELETE') {
            return;
        }

        setIsDeleting(true);
        try {
            await onConfirm(item.id);
            onClose();
        } catch (error) {
            console.error(`Error deleting ${itemType}:`, error);
        } finally {
            setIsDeleting(false);
        }
    };

    const isDeleteButtonEnabled = deleteText === 'DELETE' && !isDeleting;

    return (
        <div
            style={{
                backgroundColor: "#fff",
                padding: "20px",
                borderRadius: "8px",
                width: "500px",
                maxHeight: "90vh",
                overflowY: "auto",
                position: "relative",
            }}
        >
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    paddingBottom: "10px",
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                <h2 style={{ margin: 0, color: '#dc3545' }}>Delete {itemType.charAt(0).toUpperCase() + itemType.slice(1)}</h2>
                <button
                    onClick={onClose}
                    style={{
                        background: "none",
                        border: "none",
                        fontSize: "24px",
                        cursor: "pointer",
                        color: "#666",
                    }}
                >
                    ×
                </button>
            </div>
            
            <div className="modal-body">
                <div style={{ marginBottom: '20px' }}>
                    <p style={{ marginBottom: '15px', color: '#6c757d' }}>
                        You are about to delete the {itemType} <strong>"{item?.name}"</strong>. 
                        This action cannot be undone.
                    </p>
                    
                    <div style={{ 
                        padding: '15px', 
                        backgroundColor: '#fff3cd', 
                        border: '1px solid #ffeaa7',
                        borderRadius: '6px',
                        marginBottom: '20px'
                    }}>
                        <p style={{ margin: 0, color: '#856404' }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px' }}></i>
                            <strong>Warning:</strong> Deleting this {itemType} will:
                        </p>
                        <ul style={{ margin: '10px 0 0 20px', color: '#856404' }}>
                            {itemType === 'tour' ? (
                                <>
                                    <li>Remove all associated events</li>
                                    <li>Delete all event submissions</li>
                                    <li>Remove all event data</li>
                                </>
                            ) : (
                                <>
                                    <li>Remove all associated submissions</li>
                                    <li>Delete all submission data</li>
                                </>
                            )}
                        </ul>
                    </div>

                    <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                            To confirm deletion, type <strong>DELETE</strong> in the field below:
                        </label>
                        <input
                            type="text"
                            value={deleteText}
                            onChange={(e) => setDeleteText(e.target.value)}
                            placeholder="Type DELETE to confirm"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        />
                    </div>
                </div>
            </div>
            
            <div
                style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    padding: "20px",
                    borderTop: "1px solid #e0e0e0",
                }}
            >
                <button 
                    className="btn btn-secondary" 
                    onClick={onClose}
                    disabled={isDeleting}
                >
                    Cancel
                </button>
                <button 
                    className="btn btn-danger" 
                    onClick={handleConfirm}
                    disabled={!isDeleteButtonEnabled}
                    style={{
                        opacity: isDeleteButtonEnabled ? 1 : 0.6,
                        cursor: isDeleteButtonEnabled ? 'pointer' : 'not-allowed'
                    }}
                >
                    {isDeleting ? (
                        <>
                            <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }}></i>
                            Deleting...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-trash" style={{ marginRight: '8px' }}></i>
                            Delete {itemType.charAt(0).toUpperCase() + itemType.slice(1)}
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default TourList;